import { useApiServiceClient } from "@/modules/app/apiServicesContext";
import {
  AuthenticateRequest,
  LoginRequest,
  AuthenticateResetRequest,
  GuestRequest,
  AuthenticateReferrer,
  AuthenticateReferrerType,
  AuthenticateSignupRequest
} from "@travello/grpc-api/js/authenticate_pb";
import { DEVICE_INFO, APPLICATION_ID } from "@/modules/app/constants";
import { useMemo } from "react";

const buildReferrer = (referrerId?: string) => {
  if (referrerId) {
    // this is for signup
    const referrer = new AuthenticateReferrer();
    referrer.setReferrerType(AuthenticateReferrerType.USER);
    referrer.setReferrer(referrerId);
    return referrer;
  }
};

const useRepository = () => {
  const {
    authenticationServicePromiseClient: getAuthenticationServicePromiseClient
  } = useApiServiceClient();

  const loginAsGuest$ = async () => {
    const request = new GuestRequest();
    request.setApplicationId(APPLICATION_ID);
    request.setDeviceInfo(DEVICE_INFO);

    const client = await getAuthenticationServicePromiseClient();
    const res = await client.authenticateLoginAsGuest(request, {});

    return res.toObject();
  };

  const loginWithToken$ = async ({
    token,
    system,
    referrerId
  }: Pick<AuthenticateRequest.AsObject, "token" | "system"> & {
    referrerId?: string;
  }) => {
    const request = new AuthenticateRequest();
    request.setApplicationId(APPLICATION_ID);
    request.setDeviceInfo(DEVICE_INFO);
    request.setSystem(system);
    request.setToken(token);
    request.setReferrer(buildReferrer(referrerId));

    const client = await getAuthenticationServicePromiseClient();
    const res = await client.authenticate(request, {});
    return res.toObject();
  };

  const signupWithEmail$ = async ({
    email,
    password,
    userName,
    referrerId
  }: Pick<
    AuthenticateSignupRequest.AsObject,
    "email" | "password" | "userName"
  > & {
    referrerId?: string;
  }) => {
    const request = new AuthenticateSignupRequest();
    request.setApplicationId(APPLICATION_ID);
    request.setDeviceInfo(DEVICE_INFO);

    request.setEmail(email);
    request.setPassword(password);
    request.setUserName(userName);
    request.setReferrer(buildReferrer(referrerId));

    const client = await getAuthenticationServicePromiseClient();
    const res = await client.authenticateSignup(request, {});
    return res.toObject();
  };

  const loginWithEmail$ = async ({
    username,
    password
  }: Pick<LoginRequest.AsObject, "username" | "password">) => {
    const request = new LoginRequest();
    request.setApplicationId(APPLICATION_ID);
    request.setDeviceInfo(DEVICE_INFO);
    request.setUsername(username);
    request.setPassword(password);

    const client = await getAuthenticationServicePromiseClient();
    const res = await client.authenticateLogin(request, {});
    return res.toObject();
  };

  const resetPassword$ = async ({
    email
  }: Pick<AuthenticateResetRequest.AsObject, "email">) => {
    const request = new AuthenticateResetRequest();
    request.setApplicationId(APPLICATION_ID);
    request.setEmail(email);

    const client = await getAuthenticationServicePromiseClient();
    const res = await client.authenticateResetPassword(request, {});
    return res.toObject();
  };

  return useMemo(
    () => ({
      loginAsGuest$,
      loginWithEmail$,
      signupWithEmail$,
      loginWithToken$,
      resetPassword$
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

export default useRepository;
